import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, CircularProgress, Alert, Chip,
} from '@mui/material';
import { accountingApi } from '../../services/api';

interface PnLRow {
  accountCode: string;
  accountName: string;
  amount: number;
}

const ProfitLoss: React.FC = () => {
  const [revenues, setRevenues] = useState<PnLRow[]>([]);
  const [expenses, setExpenses] = useState<PnLRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await accountingApi.getIncomeStatement();
        const rows: PnLRow[] = Array.isArray(res.data) ? res.data : [];
        // Group by first 3 chars of account code (4000+ = Revenue, 5000+ = Expense)
        setRevenues(rows.filter(r => r.accountCode.startsWith('4')));
        setExpenses(rows.filter(r => r.accountCode.startsWith('5') || r.accountCode.startsWith('6')));
      } catch { setError('Failed to load income statement.'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const totalRevenue = revenues.reduce((s, r) => s + r.amount, 0);
  const totalExpense = expenses.reduce((s, r) => s + r.amount, 0);
  const netIncome = totalRevenue - totalExpense;
  const fmt = (v: number) => `RM${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Profit &amp; Loss Statement</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={3}>
                  <Chip label="REVENUE" color="success" size="small" sx={{ fontWeight: 600 }} />
                </TableCell>
              </TableRow>
              {revenues.length === 0 ? (
                <TableRow><TableCell colSpan={3} sx={{ pl: 4 }}><Typography color="text.secondary">No revenue accounts.</Typography></TableCell></TableRow>
              ) : revenues.map(r => (
                <TableRow key={r.accountCode} hover>
                  <TableCell sx={{ pl: 4 }}>{r.accountCode}</TableCell>
                  <TableCell>{r.accountName}</TableCell>
                  <TableCell align="right">{fmt(r.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalRevenue)}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={3} sx={{ pt: 3 }}>
                  <Chip label="EXPENSES" color="error" size="small" sx={{ fontWeight: 600 }} />
                </TableCell>
              </TableRow>
              {expenses.length === 0 ? (
                <TableRow><TableCell colSpan={3} sx={{ pl: 4 }}><Typography color="text.secondary">No expense accounts.</Typography></TableCell></TableRow>
              ) : expenses.map(r => (
                <TableRow key={r.accountCode} hover>
                  <TableCell sx={{ pl: 4 }}>{r.accountCode}</TableCell>
                  <TableCell>{r.accountName}</TableCell>
                  <TableCell align="right">{fmt(r.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Expenses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalExpense)}</TableCell>
              </TableRow>

              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', fontSize: '1.05rem' } }}>
                <TableCell colSpan={2}>
                  {netIncome >= 0 ? 'Net Income' : 'Net Loss'}
                </TableCell>
                <TableCell align="right" sx={{ color: netIncome >= 0 ? 'success.main' : 'error.main' }}>
                  {fmt(Math.abs(netIncome))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProfitLoss;
